/**
 * Budget.
 *
 * Built against taskdojo-app/src/screens/BudgetScreen.js and its BudgetContext.
 * Three collections, all keyed by userId:
 *
 *     budgetCategories  { userId, name, budgetAmount, color, icon, … }
 *     budgetEntries     { userId, categoryId, description, amount, date,
 *                         note, ref, … }
 *     budgetSettings/{uid}  { userId, overallBudget, currency, updatedAt }
 *
 * `date` on an entry is a full ISO instant, as the phone writes it, and months
 * are compared with local getFullYear/getMonth -- never a UTC slice, or a
 * purchase late on the 31st lands in the following month.
 *
 * The total is the phone's rule and is easy to get wrong: an explicit
 * `overallBudget` wins if it is set, and only otherwise does it fall back to
 * the sum of the categories. Summing unconditionally would quietly ignore the
 * figure someone actually typed.
 *
 * Money is rendered with the stored currency's symbol, never a locale's, since
 * the currency is the user's choice rather than the browser's.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ScreenHeader, SummaryBand, ActionButton, PrimaryButton, SecondaryButton,
  Field, useInputClass, raised, pressable,
} from '../dojo';
import {
  BudgetCategory, BudgetEntry,
  subscribeToBudgetCategories, subscribeToBudgetEntries,
  getBudgetSettings, updateBudgetSettings,
  createBudgetCategory, updateBudgetCategory, deleteBudgetCategory,
  addBudgetEntry, deleteBudgetEntry,
} from '../../services/firebaseService';
import { formatDateFull, toDateInput, fromDateTimeInputs } from '../../utils/datetime';

/** The phone's COLOR_OPTIONS and EMOJI_OPTIONS, verbatim. */
const COLORS = ['#E34234', '#3E8C84', '#4A6FA5', '#E8B33A', '#6C5CE7', '#4C9A2A'];
const ICONS = [
  '🍔', '🏠', '🚗', '🛒', '💊', '🎬',
  '👕', '✈️', '📚', '💡', '🎁', '💰',
  '☕', '🏋️', '📱', '🐾', '🎵', '🔧',
  '🎮', '🍿', '🎭', '🎤', '🍺', '⚽',
  '💳', '📺', '🏥', '👶', '🎓', '💇',
  '🧹', '🚌', '📦', '💼', '🛡️', '❤️',
];

const CURRENCIES = [
  { code: 'GBP', symbol: '£' }, { code: 'USD', symbol: '$' },
  { code: 'EUR', symbol: '€' }, { code: 'AUD', symbol: 'A$' },
  { code: 'CAD', symbol: 'C$' }, { code: 'JPY', symbol: '¥' },
  { code: 'INR', symbol: '₹' },
];
const SYMBOLS: Record<string, string> = Object.fromEntries(
  CURRENCIES.map((c) => [c.code, c.symbol]),
);

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const fmt = (n: number) =>
  Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Budget: React.FC = () => {
  const { state } = useApp();
  const { colors, isDark } = useTheme();
  const input = useInputClass();

  const uid = state.user?.uid;
  const now = new Date();

  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [entries, setEntries] = useState<BudgetEntry[]>([]);
  const [overallBudget, setOverallBudget] = useState(0);
  const [currency, setCurrency] = useState('GBP');
  const [loading, setLoading] = useState(true);

  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  const [catModal, setCatModal] = useState<BudgetCategory | 'new' | null>(null);
  const [catName, setCatName] = useState('');
  const [catAmount, setCatAmount] = useState('');
  const [catColor, setCatColor] = useState(COLORS[0]);
  const [catIcon, setCatIcon] = useState(ICONS[0]);

  const [entryFor, setEntryFor] = useState<BudgetCategory | null>(null);
  const [entryDesc, setEntryDesc] = useState('');
  const [entryAmount, setEntryAmount] = useState('');
  const [entryDate, setEntryDate] = useState(toDateInput(new Date()));
  const [entryNote, setEntryNote] = useState('');

  const [viewing, setViewing] = useState<BudgetCategory | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [overallDraft, setOverallDraft] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDeleteCat, setConfirmDeleteCat] = useState<BudgetCategory | null>(null);

  useEffect(() => {
    if (!uid) return;
    const unsubC = subscribeToBudgetCategories(uid, (c) => { setCategories(c); setLoading(false); });
    const unsubE = subscribeToBudgetEntries(uid, setEntries);
    getBudgetSettings(uid).then((s) => {
      if (s) { setOverallBudget(s.overallBudget || 0); setCurrency(s.currency || 'GBP'); }
    }).catch(() => {});
    return () => { unsubC(); unsubE(); };
  }, [uid]);

  const symbol = SYMBOLS[currency] || '£';

  /** Entries in the shown month, compared in local time. */
  const monthEntries = useMemo(
    () => entries.filter((e) => {
      const d = new Date(e.date);
      return !Number.isNaN(d.getTime()) && d.getFullYear() === year && d.getMonth() === month;
    }),
    [entries, year, month],
  );

  const spentIn = (categoryId: string) =>
    monthEntries.filter((e) => e.categoryId === categoryId)
      .reduce((s, e) => s + (e.amount || 0), 0);

  // An explicit overall budget wins; the category sum is only a fallback.
  const totalBudget = overallBudget > 0
    ? overallBudget
    : categories.reduce((s, c) => s + (c.budgetAmount || 0), 0);
  const totalSpent = monthEntries.reduce((s, e) => s + (e.amount || 0), 0);
  const remaining = totalBudget - totalSpent;
  const percent = totalBudget > 0 ? Math.min((totalSpent / totalBudget) * 100, 100) : 0;
  const over = totalBudget > 0 && totalSpent > totalBudget;

  const shiftMonth = (delta: number) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };

  /* ------------------------------- writes ------------------------------- */

  const openCat = (c: BudgetCategory | 'new') => {
    if (c === 'new') {
      setCatName(''); setCatAmount(''); setCatColor(COLORS[0]); setCatIcon(ICONS[0]);
    } else {
      setCatName(c.name); setCatAmount(String(c.budgetAmount ?? ''));
      setCatColor(c.color || COLORS[0]); setCatIcon(c.icon || ICONS[0]);
    }
    setCatModal(c); setError('');
  };

  const saveCat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uid || !catModal) return;
    if (!catName.trim()) { setError('Give the category a name.'); return; }
    const amount = Number(catAmount);
    if (Number.isNaN(amount) || amount < 0) { setError('Enter a budget amount.'); return; }

    setSaving(true); setError('');
    try {
      const data = { name: catName.trim(), budgetAmount: amount, color: catColor, icon: catIcon };
      if (catModal === 'new') await createBudgetCategory(uid, data);
      else await updateBudgetCategory(catModal.id, data);
      setCatModal(null);
    } catch (err: any) {
      setError(err?.message || "Couldn't save that category.");
    } finally {
      setSaving(false);
    }
  };

  const removeCat = async (c: BudgetCategory) => {
    setSaving(true);
    try {
      // deleteBudgetCategory removes the category's entries too, as the phone
      // does -- orphaned entries would still count toward the month's spend
      // with nothing to attribute them to.
      await deleteBudgetCategory(c.id);
      setConfirmDeleteCat(null);
      setCatModal(null);
      setViewing(null);
    } catch (err: any) {
      setError(err?.message || "Couldn't delete that category.");
    } finally {
      setSaving(false);
    }
  };

  const openEntry = (c: BudgetCategory) => {
    setEntryDesc(''); setEntryAmount(''); setEntryNote('');
    setEntryDate(toDateInput(new Date(year, month, Math.min(now.getDate(), 28))));
    setEntryFor(c); setError('');
  };

  const saveEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uid || !entryFor) return;
    const amount = Number(entryAmount);
    if (!entryDesc.trim()) { setError('What was it for?'); return; }
    if (Number.isNaN(amount) || amount <= 0) { setError('Enter an amount.'); return; }

    setSaving(true); setError('');
    try {
      await addBudgetEntry(uid, {
        categoryId: entryFor.id,
        description: entryDesc.trim(),
        amount,
        // Stored as a full ISO instant, matching the phone.
        date: fromDateTimeInputs(entryDate) || new Date().toISOString(),
        note: entryNote.trim(),
      });
      setEntryFor(null);
    } catch (err: any) {
      setError(err?.message || "Couldn't save that.");
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (id: string) => {
    try { await deleteBudgetEntry(id); }
    catch (err: any) { setError(err?.message || "Couldn't delete that."); }
  };

  const saveSettings = async () => {
    if (!uid) return;
    setSaving(true);
    try {
      const amount = Number(overallDraft);
      await updateBudgetSettings(uid, {
        overallBudget: Number.isNaN(amount) ? 0 : amount,
        currency,
      } as any);
      setOverallBudget(Number.isNaN(amount) ? 0 : amount);
      setSettingsOpen(false);
    } catch (err: any) {
      setError(err?.message || "Couldn't save your budget settings.");
    } finally {
      setSaving(false);
    }
  };

  const arrow = `w-9 h-9 rounded-[12px] flex items-center justify-center ${raised} ${pressable}
    ${colors.cardBorder} ${colors.card} ${colors.textSecondary}`;

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        <ScreenHeader
          title="Budget"
          action={
            <ActionButton label="New category" onClick={() => openCat('new')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            </ActionButton>
          }
        />

        <SummaryBand
          items={[
            { value: `${symbol}${fmt(totalSpent)}`, label: 'Spent' },
            { value: `${symbol}${fmt(totalBudget)}`, label: 'Budget', color: '#4A6FA5' },
            {
              value: `${symbol}${fmt(Math.abs(remaining))}`,
              label: remaining < 0 ? 'Over' : 'Left',
              color: remaining < 0 ? '#E34234' : '#4C9A2A',
            },
          ]}
        />

        {error && (
          <div role="alert" className="mx-5 mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
            px-4 py-3 text-[13px] font-semibold text-dojo-red">
            {error}
          </div>
        )}

        <div className="px-5 space-y-5">
          {/* ------------------------------ month ------------------------------ */}
          <div className={`rounded-[18px] border-2 border-b-[3px] ${colors.cardBorder} ${colors.card} p-4`}>
            <div className="flex items-center justify-between mb-4">
              <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month" className={arrow}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <h2 className="text-[19px] font-black tracking-[-0.4px]">{MONTHS[month]} {year}</h2>
              <button type="button" onClick={() => shiftMonth(1)} aria-label="Next month" className={arrow}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div className="flex items-center justify-between mb-1.5">
              <span className={`text-[12px] font-bold uppercase tracking-[0.5px] ${colors.textSecondary}`}>
                {over ? 'Over budget' : `${Math.round(percent)}% of budget`}
              </span>
              <button
                type="button"
                onClick={() => { setOverallDraft(String(overallBudget || '')); setSettingsOpen(true); }}
                className={`text-[12.5px] font-extrabold ${colors.textSecondary} hover:text-dojo-red transition-colors`}
              >
                Set budget
              </button>
            </div>
            <div className={`h-2.5 rounded-full overflow-hidden ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-200'}`}>
              <div
                className="h-full rounded-full transition-[width] duration-500"
                style={{ width: `${percent}%`, backgroundColor: over ? '#E34234' : '#4C9A2A' }}
              />
            </div>
          </div>

          {/* ---------------------------- categories ---------------------------- */}
          {loading ? (
            <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>Loading…</p>
          ) : categories.length === 0 ? (
            <div className="flex flex-col items-center text-center py-16">
              <div className={`w-[88px] h-[88px] rounded-[30px] border-2 ${colors.cardBorder} ${colors.card}
                flex items-center justify-center mb-[18px] text-[34px]`}>💰</div>
              <h2 className="text-xl font-black tracking-[-0.4px] mb-1.5">No categories yet</h2>
              <p className={`text-[15px] font-semibold mb-5 ${colors.textSecondary}`}>
                Add one for the thing you spend most on.
              </p>
              <button
                type="button"
                onClick={() => openCat('new')}
                className={`rounded-[14px] bg-dojo-red border-dojo-redDeep text-white ${raised} ${pressable}
                  px-5 pt-[13px] pb-[11px] text-[15px] font-extrabold`}
              >
                Add a category
              </button>
            </div>
          ) : (
            <ul className="space-y-2.5 list-none p-0 m-0">
              {categories.map((c) => {
                const spent = spentIn(c.id);
                const pct = c.budgetAmount > 0 ? Math.min((spent / c.budgetAmount) * 100, 100) : 0;
                const catOver = c.budgetAmount > 0 && spent > c.budgetAmount;
                return (
                  <li key={c.id}>
                    <div className={`rounded-[15px] border-2 border-b-[3px] ${colors.card} ${colors.cardBorder} p-3.5`}>
                      <div className="flex items-center gap-3">
                        <span
                          className="w-11 h-11 rounded-[14px] flex items-center justify-center text-[21px] flex-shrink-0"
                          style={{ backgroundColor: `${c.color}26` }}
                        >
                          {c.icon}
                        </span>
                        <button
                          type="button"
                          onClick={() => setViewing(c)}
                          className="flex-1 min-w-0 text-left"
                        >
                          <span className="block text-[15.5px] font-extrabold truncate">{c.name}</span>
                          <span className={`block text-[13px] font-semibold ${colors.textSecondary}`}>
                            {symbol}{fmt(spent)} of {symbol}{fmt(c.budgetAmount)}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => openEntry(c)}
                          className={`h-9 px-3 rounded-[12px] ${raised} ${pressable} flex-shrink-0
                            bg-dojo-red border-dojo-redDeep text-white text-[13px] font-extrabold`}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => openCat(c)}
                          aria-label={`Edit ${c.name}`}
                          className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0
                            ${colors.textMuted} ${isDark ? 'hover:bg-dojo-surfaceDeep' : 'hover:bg-stone-100'}`}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                              strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z"
                              strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                      <div className={`h-1.5 rounded-full overflow-hidden mt-2.5
                        ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-200'}`}>
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${pct}%`, backgroundColor: catOver ? '#E34234' : c.color }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* --------------------------- category editor --------------------------- */}
      {catModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
          onClick={() => setCatModal(null)}>
          <form
            onSubmit={saveCat}
            onClick={(e) => e.stopPropagation()}
            className={`w-full sm:max-w-lg max-h-[92vh] overflow-y-auto ${colors.card}
              border-2 ${colors.cardBorder} rounded-t-[22px] sm:rounded-[22px] p-6`}
          >
            <h2 className="text-[26px] font-black tracking-[-0.7px] mb-5">
              {catModal === 'new' ? 'New category' : 'Edit category'}
            </h2>

            <Field label="Name" className="mb-4">
              <input className={input} value={catName} onChange={(e) => setCatName(e.target.value)}
                placeholder="Groceries" autoFocus />
            </Field>

            <Field label={`Monthly budget (${symbol})`} className="mb-4">
              <input className={input} value={catAmount} onChange={(e) => setCatAmount(e.target.value)}
                placeholder="250" inputMode="decimal" />
            </Field>

            <Field label="Icon" className="mb-4">
              <div className="flex flex-wrap gap-1.5 max-h-[140px] overflow-y-auto">
                {ICONS.map((i) => (
                  <button key={i} type="button" onClick={() => setCatIcon(i)} aria-pressed={catIcon === i}
                    className={`w-10 h-10 rounded-[12px] border-2 text-[19px] flex items-center justify-center
                      ${catIcon === i ? 'border-dojo-red bg-dojo-red/10'
                        : `${colors.cardBorder} ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'}`}`}>
                    {i}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Colour" className="mb-5">
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setCatColor(c)} aria-label={c}
                    aria-pressed={catColor === c}
                    className={`w-9 h-9 rounded-xl border-2 transition-transform
                      ${catColor === c ? 'scale-110 border-current' : 'border-transparent hover:scale-105'}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
            </Field>

            {error && (
              <div role="alert" className="mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
                px-4 py-3 text-[13px] font-semibold text-dojo-red">{error}</div>
            )}

            <div className="flex gap-3">
              <PrimaryButton type="submit" disabled={saving} className="flex-1">
                {saving ? 'Saving…' : catModal === 'new' ? 'Create' : 'Save'}
              </PrimaryButton>
              {catModal !== 'new' && (
                <SecondaryButton onClick={() => setConfirmDeleteCat(catModal)} className="!text-dojo-red">
                  Delete
                </SecondaryButton>
              )}
            </div>
          </form>
        </div>
      )}

      {/* ------------------------------ new entry ------------------------------ */}
      {entryFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
          onClick={() => setEntryFor(null)}>
          <form
            onSubmit={saveEntry}
            onClick={(e) => e.stopPropagation()}
            className={`w-full sm:max-w-md ${colors.card} border-2 ${colors.cardBorder}
              rounded-t-[22px] sm:rounded-[22px] p-6`}
          >
            <div className="flex items-center gap-2.5 mb-5">
              <span className="text-[24px]">{entryFor.icon}</span>
              <h2 className="text-[24px] font-black tracking-[-0.6px]">{entryFor.name}</h2>
            </div>

            <Field label="What was it for?" className="mb-4">
              <input className={input} value={entryDesc} onChange={(e) => setEntryDesc(e.target.value)}
                placeholder="Weekly shop" autoFocus />
            </Field>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <Field label={`Amount (${symbol})`}>
                <input className={input} value={entryAmount} onChange={(e) => setEntryAmount(e.target.value)}
                  placeholder="42.50" inputMode="decimal" />
              </Field>
              <Field label="Date">
                <input type="date" className={input} value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)} />
              </Field>
            </div>

            <Field label="Note" className="mb-5">
              <input className={input} value={entryNote} onChange={(e) => setEntryNote(e.target.value)}
                placeholder="optional" />
            </Field>

            {error && (
              <div role="alert" className="mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
                px-4 py-3 text-[13px] font-semibold text-dojo-red">{error}</div>
            )}

            <div className="flex gap-3">
              <SecondaryButton onClick={() => setEntryFor(null)} className="flex-1">Cancel</SecondaryButton>
              <PrimaryButton type="submit" disabled={saving} className="flex-1">
                {saving ? 'Saving…' : 'Add spend'}
              </PrimaryButton>
            </div>
          </form>
        </div>
      )}

      {/* --------------------------- category entries --------------------------- */}
      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
          onClick={() => setViewing(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full sm:max-w-lg max-h-[85vh] overflow-y-auto ${colors.card}
              border-2 ${colors.cardBorder} rounded-t-[22px] sm:rounded-[22px] p-6`}
          >
            <div className="flex items-center gap-2.5 mb-4">
              <span className="text-[24px]">{viewing.icon}</span>
              <h2 className="text-[24px] font-black tracking-[-0.6px] flex-1">{viewing.name}</h2>
              <button type="button" onClick={() => setViewing(null)} aria-label="Close"
                className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors.textSecondary}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className={`rounded-[14px] ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'}
              border-2 ${colors.cardBorder} px-4 py-3 flex justify-between mb-4`}>
              <span>
                <span className={`block text-[11.5px] font-bold uppercase tracking-[0.5px] ${colors.textMuted}`}>Spent</span>
                <span className="block text-[18px] font-extrabold tabular-nums">{symbol}{fmt(spentIn(viewing.id))}</span>
              </span>
              <span className="text-right">
                <span className={`block text-[11.5px] font-bold uppercase tracking-[0.5px] ${colors.textMuted}`}>Budget</span>
                <span className="block text-[18px] font-extrabold tabular-nums">{symbol}{fmt(viewing.budgetAmount)}</span>
              </span>
            </div>

            <p className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2 ${colors.textMuted}`}>
              {MONTHS[month]} {year}
            </p>

            {monthEntries.filter((e) => e.categoryId === viewing.id).length === 0 ? (
              <p className={`text-[14px] font-semibold py-6 text-center ${colors.textSecondary}`}>
                Nothing recorded this month.
              </p>
            ) : (
              <ul className="space-y-2 list-none p-0 m-0">
                {monthEntries.filter((e) => e.categoryId === viewing.id).map((e) => (
                  <li key={e.id}>
                    <div className={`rounded-[12px] border-2 ${colors.cardBorder} px-3 py-2.5 flex items-center gap-3`}>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[14px] font-bold truncate">{e.description}</span>
                        <span className={`block text-[12px] font-semibold ${colors.textMuted}`}>
                          {formatDateFull(e.date)}{(e as any).note ? ` · ${(e as any).note}` : ''}
                        </span>
                      </span>
                      <span className="text-[15px] font-extrabold tabular-nums flex-shrink-0">
                        {symbol}{fmt(e.amount)}
                      </span>
                      <button type="button" onClick={() => removeEntry(e.id)} aria-label="Delete entry"
                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                          ${colors.textMuted} hover:text-dojo-red transition-colors`}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                          <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"
                            strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <PrimaryButton onClick={() => { setViewing(null); openEntry(viewing); }} className="w-full mt-5">
              Add spend
            </PrimaryButton>
          </div>
        </div>
      )}

      {/* ------------------------------ settings ------------------------------ */}
      {settingsOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
          onClick={() => setSettingsOpen(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full sm:max-w-md ${colors.card} border-2 ${colors.cardBorder}
              rounded-t-[22px] sm:rounded-[22px] p-6`}
          >
            <h2 className="text-[24px] font-black tracking-[-0.6px] mb-1">Budget settings</h2>
            <p className={`text-[13px] font-semibold mb-5 ${colors.textSecondary}`}>
              Leave the overall budget at zero to use the sum of your categories instead.
            </p>

            <Field label={`Overall monthly budget (${symbol})`} className="mb-4">
              <input className={input} value={overallDraft} onChange={(e) => setOverallDraft(e.target.value)}
                placeholder="0" inputMode="decimal" />
            </Field>

            <Field label="Currency" className="mb-5">
              <select className={input} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                ))}
              </select>
            </Field>

            <div className="flex gap-3">
              <SecondaryButton onClick={() => setSettingsOpen(false)} className="flex-1">Cancel</SecondaryButton>
              <PrimaryButton onClick={saveSettings} disabled={saving} className="flex-1">
                {saving ? 'Saving…' : 'Save'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteCat && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-5">
          <div className={`w-full max-w-sm ${colors.card} border-2 ${colors.cardBorder} rounded-[22px] p-6`}>
            <h3 className="text-xl font-black tracking-[-0.4px] mb-2">Delete this category?</h3>
            <p className={`text-[14px] font-semibold mb-5 ${colors.textSecondary}`}>
              “{confirmDeleteCat.name}” and every entry in it will be removed. This can't be undone.
            </p>
            <div className="flex gap-3">
              <SecondaryButton onClick={() => setConfirmDeleteCat(null)} className="flex-1">Cancel</SecondaryButton>
              <PrimaryButton onClick={() => removeCat(confirmDeleteCat)} disabled={saving} className="flex-1">
                {saving ? 'Deleting…' : 'Delete'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Budget;
